import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from '@angular/router';
import { AuthHttpService } from '@app/core/services/http';
import { ADMIN_ALLOWED_ROLES, FULL_ACCESS_ROLES } from '@app/core/services/http/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate, CanActivateChild {
  private readonly authService = inject(AuthHttpService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): boolean {
    return this.canAccessRoute(route);
  }

  canActivateChild(route: ActivatedRouteSnapshot): boolean {
    if (!this.canAccessRoute(route)) return false;

    // Агенту «Только реклама» открыт только кабинет агента («Мой маркетинг»)
    if (this.authService.isAdvertisingOnlyAgent() && !this.isAdvertisingOnlyRoute(route)) {
      this.router.navigate(this.authService.getHomeRoute());
      return false;
    }

    return true;
  }

  private canAccessRoute(route: ActivatedRouteSnapshot): boolean {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['login']);
      return false;
    }

    if (!this.authService.hasAnyRole([...ADMIN_ALLOWED_ROLES])) {
      this.authService.clearAuthStorage();
      this.router.navigate(['login']);
      return false;
    }

    const allowedRoles = this.getAllowedRoles(route);
    const isAllowed = this.authService.hasAnyRole(allowedRoles);
    if (!isAllowed) {
      this.router.navigate(this.authService.getHomeRoute());
      return false;
    }

    return true;
  }

  private isAdvertisingOnlyRoute(route: ActivatedRouteSnapshot): boolean {
    return route.pathFromRoot.some(snapshot => snapshot.data?.['advertisingOnlyAccess'] === true);
  }

  private getAllowedRoles(route: ActivatedRouteSnapshot): string[] {
    const routeWithRoles = [...route.pathFromRoot]
      .reverse()
      .find(snapshot => Array.isArray(snapshot.data?.['roles']));

    return (routeWithRoles?.data?.['roles'] as string[] | undefined) ?? [...FULL_ACCESS_ROLES];
  }
}
